import { DiwaAPI } from './client'

export type MissionStatus = 'Active' | 'Draft' | 'Waiting for Review' | 'Blocked' | 'Complete'
export type MissionPriority = 'High' | 'Medium' | 'Low'

/**
 * Grain's StepStatus vocabulary (grain_contracts.workflow), not the retired display words.
 * The backend rejects anything else with a 400 since migration 0007.
 */
export type StepStatus = 'pending' | 'running' | 'awaiting_input' | 'awaiting_gate' | 'done' | 'failed'

/** Mirrors grain_contracts.workflow.StepRecord.to_dict() — `id` is the step's name. */
export interface MissionStep {
  id: string
  status: StepStatus
  gate?: 'none' | 'review'
  artifact?: string
  attempts?: number
  error?: string
}

export interface FitCheck {
  key: string
  label: string
  verdict: 'have' | 'partial' | 'gap' | 'unknown'
  detail: string
}

export interface FitBrief {
  checks: FitCheck[]
  recommendation: 'pursue' | 'caution' | 'pass'
  summary: string
}

export interface Mission {
  id: string
  title: string
  goal: string
  status: MissionStatus
  priority: MissionPriority
  domain: string
  target: string
  protocol: string
  createdAt: string
  steps: MissionStep[]
  brief?: FitBrief | null
  outcome?: 'won' | 'lost' | 'no_bid' | 'done' | null
  outcomeAt?: string | null
}

export interface MissionDraft {
  target: string
  goal: string
  domain: string
  protocol: string
  priority: MissionPriority
}

export interface CreateMissionInput {
  title: string
  goal: string
  status: MissionStatus
  priority: MissionPriority
  domain: string
  target: string
  protocol: string
  steps: MissionStep[]
}

/**
 * PATCH /missions/{id} — every field optional and independently updatable. A status-only
 * body is the legacy shape (still honored); title/goal/steps are the per-mission page edits.
 * Backend validates `title` non-blank when present.
 */
export interface MissionPatch {
  title?: string
  goal?: string
  steps?: MissionStep[]
  status?: MissionStatus
  /** Re-triage — validated server-side to the High/Medium/Low set. */
  priority?: MissionPriority
  /** Re-home the mission to another domain — a slug string. */
  domain?: string
}

interface MissionRaw extends Omit<Mission, 'createdAt' | 'outcomeAt'> {
  created_at: string
  outcome_at?: string | null
}

function fromRaw(m: MissionRaw): Mission {
  const { created_at, outcome_at, ...rest } = m
  return { ...rest, createdAt: created_at, outcomeAt: outcome_at ?? null }
}

/** Mirrors grain.workflow-run/v1 (grain_contracts.workflow.Run.to_dict()). */
export interface WorkflowRun {
  run_id: string
  protocol: string
  status: 'pending' | 'running' | 'awaiting_input' | 'awaiting_gate' | 'done' | 'failed'
  cursor: string
  steps: MissionStep[]
  created: string | null
  updated: string | null
}

export interface RunDetail {
  run: WorkflowRun
  /** step artifact path -> the Familiar's authored content */
  artifacts: Record<string, string>
}

/** What the composer drafts from a goal — shown for judgment, created only on approval. */
export interface MissionProposal {
  title: string
  goal: string
  target: string
  domain: string
  protocol: string
  steps: MissionStep[]
}

/** One step in a recipe's skeleton — a label the operator reads, and whether it pauses at a
 *  review gate. Distinct from MissionStep: a template step carries no live `status` (nothing
 *  has run yet), and its `label` is the display name (the from-template create seeds `id`). */
export interface MissionTemplateStep {
  id: string
  label: string
  gate?: 'none' | 'review'
}

/**
 * A mission RECIPE from the owner-agnostic registry (GET /missions/templates). The client
 * groups these into sphere shelves by `tags` (mirroring the domain-template shelves) and
 * instantiates one into a DRAFT mission via POST /missions/from-template.
 *
 * `goal_template` may carry a `{target}` placeholder — when it does, the picker prompts for a
 * target and the server fills it. Extends `Tagged` (via structural `tags`) so lib/sphereShelves
 * helpers (allShelfTags / filterByTags / groupByShelf) apply directly.
 */
export interface MissionTemplate {
  key: string
  name: string
  gloss: string
  domain: string
  tags: string[]
  protocol: string
  goal_template: string
  steps: MissionTemplateStep[]
}

/** Body for POST /missions/from-template — `target` fills the recipe's `{target}` placeholder. */
export interface FromTemplateInput {
  key: string
  target?: string
  domain?: string
}

/** Where a proposal came from — a fresh signal that crossed your rules, or a newly-arrived star. */
export type ProposalSourceKind = 'signal' | 'entity'

/**
 * A LIVE mission proposal — Tala reading your fresh signals and new stars and suggesting a
 * recipe to run on one. Computed on every GET /missions/proposals from the current signals +
 * recent entities MINUS the suppression table (dismissed / already-promoted); never persisted
 * until promoted. `id` is a deterministic hash of (source_kind, source_id, recipe_key), so
 * dismiss and promote are idempotent without a pre-written row.
 *
 * Distinct from `MissionProposal` (the goal-first DRAFT the composer returns): this one carries
 * no steps yet — promoting it instantiates the named recipe via the from-template path.
 */
export interface ProposedMission {
  id: string
  recipe_key: string
  recipe_name: string
  target: string
  domain: string
  reason: string
  source_kind: ProposalSourceKind
  source_id: string
}

export class MissionsAPI {
  private api: DiwaAPI

  constructor(baseUrl: string) {
    this.api = new DiwaAPI(baseUrl)
  }

  async list(): Promise<Mission[]> {
    return (await this.api.get<MissionRaw[]>('/missions')).map(fromRaw)
  }

  /** Single-fetch for the per-mission page. Owner-scoped — a 404 means not yours / not found. */
  async get(id: string): Promise<Mission> {
    return fromRaw(await this.api.get<MissionRaw>(`/missions/${id}`))
  }

  /** Open a fresh run of the mission's protocol — every step pending, cursor on the first. */
  startRun(missionId: string): Promise<{ run: WorkflowRun }> {
    return this.api.post<{ run: WorkflowRun }>(`/missions/${missionId}/runs`, {})
  }

  listRuns(missionId: string): Promise<{ runs: WorkflowRun[] }> {
    return this.api.get<{ runs: WorkflowRun[] }>(`/missions/${missionId}/runs`)
  }

  /** Drive the cursor step once: the Familiar authors the artifact and the run advances. */
  runStep(missionId: string, runId: string): Promise<{ run: WorkflowRun }> {
    return this.api.post<{ run: WorkflowRun }>(`/missions/${missionId}/runs/${runId}/step`, {})
  }

  /** Reject may carry direction — the Familiar follows it when redoing the step. */
  decideGate(
    missionId: string,
    runId: string,
    decision: 'approve' | 'reject',
    guidance?: string,
  ): Promise<{ run: WorkflowRun }> {
    return this.api.post<{ run: WorkflowRun }>(`/missions/${missionId}/runs/${runId}/gate`, {
      decision,
      ...(guidance?.trim() ? { guidance: guidance.trim() } : {}),
    })
  }

  runDetail(missionId: string, runId: string): Promise<RunDetail> {
    return this.api.get<RunDetail>(`/missions/${missionId}/runs/${runId}`)
  }

  /** Goal in, mission draft out. Nothing is created until the operator approves the draft. */
  propose(goal: string): Promise<{ proposal: MissionProposal }> {
    return this.api.post<{ proposal: MissionProposal }>('/missions/propose', { goal })
  }

  /** The recipe registry — owner-agnostic template data, grouped/filtered client-side. */
  async listTemplates(): Promise<MissionTemplate[]> {
    return (await this.api.get<{ templates: MissionTemplate[] }>('/missions/templates')).templates
  }

  /** Instantiate a recipe into a DRAFT mission (seeds steps + fills {target}), owner-scoped.
   *  Returns the created mission — the caller navigates to /missions/:id to edit it. */
  async fromTemplate(input: FromTemplateInput): Promise<Mission> {
    return fromRaw(await this.api.post<MissionRaw>('/missions/from-template', input))
  }

  /** Tala's LIVE suggestions — recipes to run on your fresh signals / new stars, computed
   *  per-request minus whatever you've dismissed or already promoted. Empty when nothing fits. */
  async listProposals(): Promise<ProposedMission[]> {
    return (await this.api.get<{ proposals: ProposedMission[] }>('/missions/proposals')).proposals
  }

  /** Suppress a proposal so it never re-surfaces (also marks its source signal 'seen'). */
  async dismissProposal(id: string): Promise<void> {
    await this.api.post<unknown>(`/missions/proposals/${id}/dismiss`, {})
  }

  /** Accept a proposal: instantiate its recipe on `target` (defaults to the proposal's own
   *  target when omitted), suppress the proposal, and mark its source signal 'acted'. Returns
   *  the created mission — the caller navigates to /missions/:id. */
  async promoteProposal(id: string, target?: string): Promise<Mission> {
    return fromRaw(
      await this.api.post<MissionRaw>(
        `/missions/proposals/${id}/promote`,
        target?.trim() ? { target: target.trim() } : {},
      ),
    )
  }

  /** Manual edit of the gated artifact — approval then ships the operator's version. */
  editArtifact(
    missionId: string,
    runId: string,
    path: string,
    content: string,
  ): Promise<{ edited: boolean; path: string }> {
    return this.api.put<{ edited: boolean; path: string }>(
      `/missions/${missionId}/runs/${runId}/artifact`,
      { path, content },
    )
  }

  /** ✦ Revise — the Familiar rewrites the gated artifact to an instruction, in place. */
  reviseArtifact(
    missionId: string,
    runId: string,
    path: string,
    instruction: string,
  ): Promise<{ edited: boolean; path: string; content: string }> {
    return this.api.post<{ edited: boolean; path: string; content: string }>(
      `/missions/${missionId}/runs/${runId}/artifact/revise`,
      { path, instruction },
    )
  }

  /** ↻ Regenerate — re-author the gated step's artifact from scratch, no direction given. */
  regenerateArtifact(
    missionId: string,
    runId: string,
    path: string,
  ): Promise<{ edited: boolean; path: string; content: string }> {
    return this.api.post<{ edited: boolean; path: string; content: string }>(
      `/missions/${missionId}/runs/${runId}/artifact/regenerate`,
      { path },
    )
  }

  async create(input: CreateMissionInput): Promise<Mission> {
    return fromRaw(await this.api.post<MissionRaw>('/missions', input))
  }

  /** Partial mission edit — any subset of title/goal/steps/status. Returns the fresh mission. */
  async update(id: string, patch: MissionPatch): Promise<Mission> {
    return fromRaw(await this.api.patch<MissionRaw>(`/missions/${id}`, patch))
  }

  /** Back-compat helper — a status-only PATCH, the shape callers used before the field opened up. */
  updateStatus(id: string, status: MissionStatus): Promise<Mission> {
    return this.update(id, { status })
  }

  remove(id: string): Promise<{ deleted: boolean }> {
    return this.api.del<{ deleted: boolean }>(`/missions/${id}`)
  }
}
